// src/components/ProductsPage/ProductDetailPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../UI/Card";
import Button from "../UI/Button";
import { useTranslation } from "react-i18next";

import ProductFormModal from "./ProductFormModal";
import ComponentsSection from "./packaging/ComponentsSection";
import {
  fetchProduct,
  fetchCities,
  fetchSuppliers,
  buildProductFormData,
  updateProduct,
  deleteProduct,
  BASE_URL,
} from "./requests";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("ProductsPage");

  const [product, setProduct] = useState(null);
  const [cities, setCities] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [p, c, s] = await Promise.all([
        fetchProduct(id),
        fetchCities(),
        fetchSuppliers(),
      ]);
      setProduct(p);
      setCities(c);
      setSuppliers(s);
    } catch (e) {
      setError(e.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = () => {
    setEditingProduct({
      ...product,
      supplier_id: product.supplier?.id || "",
      production_city: product.production_city || "",
      production_city_text: "",
    });
  };

  const closeEdit = () => setEditingProduct(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;
    setSaving(true);
    try {
      const fd = buildProductFormData(editingProduct);
      await updateProduct(editingProduct.id, fd);
      closeEdit();
      await load();
    } catch (err) {
      alert(err.message || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteProduct(product.id);
      navigate("/products");
    } catch (err) {
      alert(err.message || "Ошибка удаления");
      setDeleting(false);
    }
  };

  if (loading) return <div className="p-10">Загрузка...</div>;
  if (error) return <div className="p-10 text-red-600">{error}</div>;
  if (!product) return <div className="p-10">Товар не найден</div>;

  return (
    <div className="pt-20 px-4 max-w-5xl mx-auto pb-8">
      {/* BACK */}
      <div className="mb-4">
        <Button variant="secondary" onClick={() => navigate("/products")}>
          ← {t("back")}
        </Button>
      </div>

      {/* HEADER + ACTIONS */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">
            {product.name_en} / {product.name_cn}
          </h1>
          <p className="text-gray-500 mt-1">ID: {product.id}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="edit" onClick={openEdit}>
            Редактировать
          </Button>
          <Button variant="danger" onClick={() => setConfirmDelete(true)}>
            Удалить
          </Button>
        </div>
      </div>

      {/* MAIN CARD */}
      <Card className="p-6 space-y-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div>
            {product.image ? (
              <img
                src={
                  product.image.startsWith("http")
                    ? product.image
                    : `${BASE_URL}${product.image}`
                }
                alt={product.name_en}
                className="w-64 h-64 object-cover rounded-xl border"
              />
            ) : (
              <div className="w-64 h-64 flex items-center justify-center bg-gray-200 text-gray-500 rounded-xl">
                {t("no_image")}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <p>
              <b>{t("brand")}:</b> {product.brand ? "Да" : "Нет"}
            </p>
            <p>
              <b>{t("composition")}:</b> {product.composition || "-"}
            </p>
            <p>
              <b>{t("purchase_price")}:</b> {product.purchase_price || "-"} ¥
            </p>

            <div className="space-y-1">
              <b>Цена:</b>
              {product.price_tiers?.length > 0 ? (
                product.price_tiers.map((tier) => (
                  <p key={tier.id}>
                    <b>
                      {tier.min_qty} - {tier.max_qty ?? "∞"} шт:
                    </b>{" "}
                    {tier.price} ¥
                  </p>
                ))
              ) : (
                <p className="text-gray-400">нет шкалы цен</p>
              )}
            </div>

            <p>
              <b>{t("production_city")}:</b>{" "}
              {product.production_city_data?.name
                ? `${product.production_city_data.name}, ${product.production_city_data.country}`
                : "-"}
            </p>
            <p>
              <b>{t("supplier")}:</b> {product.supplier?.name || "-"}
            </p>

            <div>
              Данные для таможни
              <p>Название для китайской таможни: {product.custom_name_cn || "-"}</p>
              <p>Название для российской таможни: {product.custom_name_ru || "-"}</p>
              <p>Вес нетто: {product.net_weight || "-"}</p>
              <p>Код ТН ВЭД: {product.russian_hs_code || "-"}</p>
              <p>Китайский HS код: {product.chinese_hs_code || "-"}</p>
              <p>Код ГНГ: {product.gng_code || "-"}</p>
              <p>Регион производства: {product.region_of_production || "-"}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* PACKAGING */}
      <ComponentsSection productId={product.id} />

      {/* MODAL: EDIT PRODUCT */}
      {editingProduct && (
        <ProductFormModal
          editingProduct={editingProduct}
          setEditingProduct={setEditingProduct}
          handleSubmit={handleSubmit}
          closeForm={closeEdit}
          cities={cities}
          suppliers={suppliers}
        />
      )}

      {/* MODAL: DELETE PRODUCT */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-2">Удалить товар?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Товар «{product.name_en}» будет удалён вместе с упаковками. Это
              действие необратимо.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                Отмена
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Удаление..." : "Удалить"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}